import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';

const Breadcrumbs = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(x => x);
  const [projectTitle, setProjectTitle] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    // If we're on a project details page, fetch the project title
    if (pathnames[0] === 'verkefni' && pathnames[1]) {
      fetchProjectTitle(pathnames[1]);
    }
  }, [location]);

  const fetchProjectTitle = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('title')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (data) {
        setProjectTitle(data.title);
      }
    } catch (error) {
      console.error('Error fetching project title:', error);
    }
  };

  // Map paths to translation keys
  const getTranslationKey = (path: string): string => {
    // Services section
    if (path === 'thjonusta') return 'nav.services.title';
    if (path === 'vefsidugerd') return 'nav.services.webDevelopment.title';
    if (path === 'vefhonnun') return 'nav.services.webDesign.title';
    if (path === 'vefthroun') return 'nav.services.webProgramming.title';
    if (path === 'radgjof') return 'nav.services.consulting.title';
    if (path === 'markadssetning') return 'nav.services.marketing.title';
    if (path === 'leitarvelabestun') return 'nav.services.seo.title';
    if (path === 'greiningar') return 'nav.services.analytics.title';
    if (path === 'vidhald') return 'nav.services.maintenance.title';

    // Solutions section
    if (path === 'lausnir') return 'nav.solutions.title';
    if (path === 'gervigreind') return 'nav.solutions.ai.title';
    if (path === 'askriftarkerfi') return 'nav.solutions.subscription.title';
    if (path === 'bokunarkerfi') return 'nav.solutions.booking.title';
    if (path === 'netverslun') return 'nav.solutions.ecommerce.title';
    if (path === 'kassakerfi') return 'nav.solutions.pos.title';
    if (path === 'wordpress') return 'nav.solutions.wordpress.title';
    if (path === 'wordpress-express') return 'nav.solutions.wordpressExpress.title';
    if (path === 'vefhysing') return 'nav.solutions.hosting.title';
    if (path === 'tolvupostur') return 'nav.solutions.email.title';
    if (path === 'vefumsjon') return 'nav.solutions.management.title';
    if (path === 'netspjall') return 'nav.solutions.chat.title';

    // Main sections
    const pathMap: { [key: string]: string } = {
      'verkefni': 'nav.projects',
      'frettir': 'nav.news',
      'hafa-samband': 'nav.contact',
      'fa-tilbod': 'nav.GetQuote',
      'fyrirtaekid': 'nav.company',
      'verdskra': 'nav.pricing',
      'thjonustupakkar': 'nav.servicePackages',
      'thjonustubord': 'nav.serviceDesk',
      'panta': 'nav.orderNow'
    };

    return pathMap[path] || path;
  };

  if (pathnames.length === 0) return null;

  return (
    <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-8">
      <Link to="/" className="hover:text-[#EA295A] transition-colors">
        {t('nav.home')}
      </Link>
      {pathnames.map((name, index) => {
        const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
        const isLast = index === pathnames.length - 1;

        // If this is a project detail page and we're on the last segment
        const isProjectDetail = pathnames[0] === 'verkefni' && index === 1;
        const displayName = isProjectDetail ? projectTitle : t(getTranslationKey(name));

        return (
          <React.Fragment key={name}>
            <ChevronRight className="w-4 h-4" />
            {isLast ? (
              <span className="text-[#EA295A] font-medium">
                {displayName}
              </span>
            ) : (
              <Link
                to={routeTo}
                className="hover:text-[#EA295A] transition-colors"
              >
                {displayName}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default Breadcrumbs;